import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import PredictionServiceNav from '../components/predictionService/PredictionServiceNav';
import LegalFooter from '../components/legal/LegalFooter';

const API_BASE = 'https://chiriri-keiba.com';
const NOTE_SERVICE_OPTIONS = [
  { key: 'nar', label: '地方競馬', caption: 'NAR' },
  { key: 'keirin', label: '競輪', caption: 'KEIRIN' },
  { key: 'jra', label: '中央競馬', caption: 'JRA' },
];

const STATUS_META = {
  drafted: {
    label: '下書き',
    textColor: '#ffe6a7',
    borderColor: 'rgba(255, 179, 71, 0.35)',
    background: 'rgba(255, 179, 71, 0.12)',
  },
  published: {
    label: '公開済み',
    textColor: '#96f3d0',
    borderColor: 'rgba(54, 211, 153, 0.35)',
    background: 'rgba(54, 211, 153, 0.12)',
  },
  error: {
    label: 'エラー',
    textColor: '#ff9494',
    borderColor: 'rgba(255, 80, 80, 0.35)',
    background: 'rgba(255, 80, 80, 0.12)',
  },
};

const PAID_META = {
  paid: { label: '有料', textColor: '#ffd580', borderColor: 'rgba(255, 179, 71, 0.35)', background: 'rgba(255, 179, 71, 0.10)' },
  free: { label: '無料', textColor: '#8dd4ff', borderColor: 'rgba(92, 160, 219, 0.35)', background: 'rgba(92, 160, 219, 0.10)' },
};

const createEmptyPageData = (targetDate, service) => ({
  file_exists: false,
  service,
  requested_date: targetDate,
  target_date: targetDate,
  generated_at: null,
  count: 0,
  source_path: '',
  notes: [],
});

const getTodayIsoDate = () => {
  const now = new Date();
  const localNow = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localNow.toISOString().slice(0, 10);
};

const buildNoteKey = (note) =>
  [note.race_id, note.umaban, note.status, note.updated_at].filter(Boolean).join('_');

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const formatCountLabel = (notes, status) =>
  notes.filter((note) => note.status === status).length;

const getStatusMeta = (status) => STATUS_META[status] || STATUS_META.drafted;
const getServiceLabel = (service) =>
  NOTE_SERVICE_OPTIONS.find((option) => option.key === service)?.label || '予想Note';
const buildPickLabel = (note) =>
  note.umaban ? `${note.umaban}番 ${note.entry_horse_name || '-'}` : note.entry_horse_name || '-';
const buildRaceLabel = (note) =>
  [note.place_name, note.race_label].filter(Boolean).join('');
const buildPublicNoteUrl = (note) => note.public_url || '';
const buildInternalNoteUrl = (note) => note.note_url || note.editor_url || note.publish_url || '';
const buildAnnouncementMessage = (note) => {
  const raceLabel = buildRaceLabel(note) || '対象レース';
  const url = buildPublicNoteUrl(note);
  const thirdPick = note.third_pick_text ? `${note.third_pick_text}\n` : '';
  return `${raceLabel}の予想投稿しました！
${thirdPick}まもなく発送予定ですので、下記から是非ご覧ください
${url}`;
};

const copyText = async (text) => {
  if (!text) {
    throw new Error('copy target is empty');
  }
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'absolute';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
};

const FieldRow = ({ label, value, mono = false }) => (
  <Box
    sx={{
      display: 'grid',
      gap: 1.25,
      gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
      alignItems: 'start',
    }}
  >
    <Typography sx={{ color: 'rgba(181, 216, 240, 0.72)', fontSize: 13 }}>{label}</Typography>
    <Typography
      sx={{
        color: '#eef8ff',
        fontSize: 14,
        wordBreak: 'break-all',
        fontFamily: mono ? '"SFMono-Regular", Consolas, "Liberation Mono", monospace' : 'inherit',
      }}
    >
      {value || '-'}
    </Typography>
  </Box>
);

function NotePostsPage() {
  const defaultDate = getTodayIsoDate();
  const [selectedService, setSelectedService] = useState('nar');
  const [selectedDate, setSelectedDate] = useState(getTodayIsoDate);
  const [pageData, setPageData] = useState(() => createEmptyPageData(defaultDate, 'nar'));
  const [selectedKey, setSelectedKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [copyStatus, setCopyStatus] = useState('');

  useEffect(() => {
    let cancelled = false;

    const fetchNotePosts = async () => {
      setLoading(true);
      setErrorMsg('');
      setPageData(createEmptyPageData(selectedDate, selectedService));
      try {
        const response = await axios.get(`${API_BASE}/api/note_posts/live`, {
          params: { date: selectedDate, service: selectedService },
        });
        if (!cancelled) {
          setPageData(response.data || createEmptyPageData(selectedDate, selectedService));
        }
      } catch (error) {
        console.error('note一覧取得失敗:', error);
        if (!cancelled) {
          setErrorMsg('note一覧JSONの取得に失敗しました。');
          setPageData({
            ...createEmptyPageData(selectedDate, selectedService),
            source_path: '',
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchNotePosts();

    return () => {
      cancelled = true;
    };
  }, [selectedDate, selectedService]);

  useEffect(() => {
    if (!pageData.notes.length) {
      setSelectedKey('');
      return;
    }
    setSelectedKey((current) => {
      if (current && pageData.notes.some((note) => buildNoteKey(note) === current)) {
        return current;
      }
      return buildNoteKey(pageData.notes[0]);
    });
  }, [pageData.notes]);

  const selectedNote = useMemo(() => {
    if (!pageData.notes.length) return null;
    return pageData.notes.find((note) => buildNoteKey(note) === selectedKey) || pageData.notes[0];
  }, [pageData.notes, selectedKey]);

  const publishedCount = useMemo(
    () => formatCountLabel(pageData.notes, 'published'),
    [pageData.notes]
  );
  const draftedCount = useMemo(
    () => formatCountLabel(pageData.notes, 'drafted'),
    [pageData.notes]
  );
  const errorCount = useMemo(
    () => formatCountLabel(pageData.notes, 'error'),
    [pageData.notes]
  );

  useEffect(() => {
    if (!copyStatus) return undefined;
    const timer = window.setTimeout(() => setCopyStatus(''), 2500);
    return () => window.clearTimeout(timer);
  }, [copyStatus]);

  const handleCopy = async (text, successMessage) => {
    try {
      await copyText(text);
      setCopyStatus(successMessage);
    } catch (error) {
      console.error('コピー失敗:', error);
      setCopyStatus('コピーに失敗しました。');
    }
  };

  if (loading && !pageData.notes.length) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          bgcolor: '#050c16',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at 15% 0%, rgba(43,132,255,0.22), transparent 28%), radial-gradient(circle at 90% 10%, rgba(50,255,226,0.14), transparent 24%), linear-gradient(180deg, #02060d 0%, #07111d 46%, #03070f 100%)',
        color: '#eef8ff',
        fontFamily: '"Avenir Next", "Yu Gothic", "Hiragino Sans", sans-serif',
      }}
    >
      <Box sx={{ maxWidth: 1480, mx: 'auto', px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 3, sm: 4 } }}>
        <Stack spacing={3}>
          <PredictionServiceNav />

          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 4,
              color: 'inherit',
              border: '1px solid rgba(92, 160, 219, 0.2)',
              background:
                'linear-gradient(180deg, rgba(10,24,41,0.92), rgba(4,10,20,0.95))',
              boxShadow: '0 24px 60px rgba(0, 0, 0, 0.28)',
            }}
          >
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', lg: 'center' }}
                spacing={2}
              >
                <Box>
                  <Typography
                    sx={{
                      color: '#71dbff',
                      fontSize: 12,
                      letterSpacing: '0.24em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Note Monitor
                  </Typography>
                  <Typography sx={{ mt: 1, fontSize: { xs: 24, sm: 30 }, fontWeight: 700 }}>
                    予想Note管理
                  </Typography>
                  <Typography sx={{ mt: 1, color: 'rgba(203, 229, 245, 0.72)', fontSize: 14 }}>
                    地方競馬と競輪の `note_posts_YYYYMMDD.json` を切り替えて、レース単位の Note 状態と詳細情報を確認できます。
                  </Typography>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', lg: 'auto' } }}>
                  {NOTE_SERVICE_OPTIONS.map((option) => {
                    const isActive = option.key === selectedService;
                    return (
                      <Button
                        key={option.key}
                        onClick={() => setSelectedService(option.key)}
                        sx={{
                          minWidth: 120,
                          px: 2,
                          py: 1.1,
                          borderRadius: 3,
                          textTransform: 'none',
                          alignItems: 'flex-start',
                          border: '1px solid',
                          borderColor: isActive
                            ? 'rgba(108, 247, 255, 0.42)'
                            : 'rgba(92, 160, 219, 0.18)',
                          background: isActive
                            ? 'linear-gradient(135deg, rgba(22,111,199,0.94), rgba(14,54,109,0.94))'
                            : 'linear-gradient(180deg, rgba(9,20,35,0.88), rgba(6,14,25,0.88))',
                          color: '#eef8ff',
                          boxShadow: isActive ? '0 0 22px rgba(76, 186, 255, 0.18)' : 'none',
                        }}
                      >
                        <Box sx={{ textAlign: 'left' }}>
                          <Typography sx={{ fontWeight: 700 }}>{option.label}</Typography>
                          <Typography sx={{ mt: 0.4, fontSize: 11, color: 'rgba(200, 228, 245, 0.68)' }}>
                            {option.caption}
                          </Typography>
                        </Box>
                      </Button>
                    );
                  })}

                  <TextField
                    type="date"
                    label="対象日"
                    value={selectedDate}
                    onChange={(event) => setSelectedDate(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      minWidth: { xs: '100%', sm: 220 },
                      '& .MuiOutlinedInput-root': {
                        color: '#ecf9ff',
                        bgcolor: 'rgba(7, 17, 31, 0.86)',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(92, 160, 219, 0.26)',
                      },
                      '& .MuiInputLabel-root': {
                        color: '#9ed7ff',
                      },
                      '& input::-webkit-calendar-picker-indicator': {
                        filter: 'invert(0.9)',
                      },
                    }}
                  />
                </Stack>
              </Stack>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  label={getServiceLabel(pageData.service || selectedService)}
                  sx={{ color: '#eef8ff', bgcolor: 'rgba(56, 104, 162, 0.28)' }}
                />
                <Chip
                  label={`対象日 ${formatDate(pageData.target_date)}`}
                  sx={{ color: '#eef8ff', bgcolor: 'rgba(56, 104, 162, 0.28)' }}
                />
                <Chip
                  label={`全 ${pageData.count} 件`}
                  sx={{ color: '#eef8ff', bgcolor: 'rgba(56, 104, 162, 0.28)' }}
                />
                <Chip
                  label={`公開 ${publishedCount} 件`}
                  sx={{ color: '#96f3d0', bgcolor: 'rgba(54, 211, 153, 0.12)' }}
                />
                <Chip
                  label={`下書き ${draftedCount} 件`}
                  sx={{ color: '#ffe6a7', bgcolor: 'rgba(255, 179, 71, 0.12)' }}
                />
                {errorCount > 0 && (
                  <Chip
                    label={`エラー ${errorCount} 件`}
                    sx={{ color: '#ff9494', bgcolor: 'rgba(255, 80, 80, 0.12)' }}
                  />
                )}
              </Stack>

              <Alert
                severity={errorMsg ? 'error' : pageData.file_exists ? 'info' : 'warning'}
                sx={{
                  color: errorMsg ? '#ffe2e0' : pageData.file_exists ? '#eaf8ff' : '#fff5d6',
                  bgcolor: errorMsg
                    ? 'rgba(220, 38, 38, 0.12)'
                    : pageData.file_exists
                      ? 'rgba(42, 125, 255, 0.12)'
                      : 'rgba(255, 179, 71, 0.12)',
                  border: '1px solid rgba(100, 171, 255, 0.2)',
                }}
              >
                {errorMsg ||
                  (pageData.file_exists
                  ? `${getServiceLabel(pageData.service || selectedService)} の対象JSONを表示中です。生成日時 ${formatDateTime(pageData.generated_at)}`
                  : `${selectedDate} の ${getServiceLabel(selectedService)} note_posts JSON は未配置です。`)}
              </Alert>
            </Stack>
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', xl: 'minmax(420px, 520px) minmax(0, 1fr)' },
            }}
          >
            <Paper
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 4,
                color: 'inherit',
                border: '1px solid rgba(92, 160, 219, 0.2)',
                background:
                  'linear-gradient(180deg, rgba(10,24,41,0.92), rgba(4,10,20,0.95))',
              }}
            >
              <Stack spacing={2}>
                <Typography sx={{ fontSize: 20, fontWeight: 700 }}>一覧表示</Typography>

                {!pageData.notes.length ? (
                  <Alert
                    severity="warning"
                    sx={{
                      color: '#fff5d6',
                      bgcolor: 'rgba(255, 179, 71, 0.12)',
                      border: '1px solid rgba(255, 187, 86, 0.2)',
                    }}
                  >
                    表示できる Note 一覧がありません。
                  </Alert>
                ) : (
                  <Stack spacing={1.25}>
                    {pageData.notes.map((note) => {
                      const isSelected = buildNoteKey(note) === buildNoteKey(selectedNote || {});
                      const statusMeta = getStatusMeta(note.status);
                      return (
                        <Button
                          component="div"
                          key={buildNoteKey(note)}
                          onClick={() => setSelectedKey(buildNoteKey(note))}
                          sx={{
                            width: '100%',
                            minWidth: 0,
                            px: 2,
                            py: 1.5,
                            borderRadius: 3,
                            textTransform: 'none',
                            alignItems: 'stretch',
                            justifyContent: 'flex-start',
                            border: '1px solid',
                            borderColor: isSelected
                              ? 'rgba(108, 247, 255, 0.42)'
                              : 'rgba(92, 160, 219, 0.18)',
                            background: isSelected
                              ? 'linear-gradient(135deg, rgba(22,111,199,0.94), rgba(14,54,109,0.94))'
                              : 'linear-gradient(180deg, rgba(9,20,35,0.88), rgba(6,14,25,0.88))',
                            color: '#eef8ff',
                            boxShadow: isSelected ? '0 0 22px rgba(76, 186, 255, 0.18)' : 'none',
                          }}
                        >
                          <Stack spacing={1} sx={{ width: '100%', textAlign: 'left' }}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              spacing={1}
                            >
                              <Box>
                                <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                                  {note.place_name} {note.race_label}
                                </Typography>
                                <Typography
                                  sx={{ mt: 0.5, color: 'rgba(200, 228, 245, 0.68)', fontSize: 12 }}
                                >
                                  発走 {note.start_time_live || '-'} / race_id {note.race_id || '-'}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                                {note.is_paid !== undefined && (
                                  <Chip
                                    label={note.is_paid ? PAID_META.paid.label : PAID_META.free.label}
                                    size="small"
                                    sx={{
                                      color: note.is_paid ? PAID_META.paid.textColor : PAID_META.free.textColor,
                                      border: `1px solid ${note.is_paid ? PAID_META.paid.borderColor : PAID_META.free.borderColor}`,
                                      bgcolor: note.is_paid ? PAID_META.paid.background : PAID_META.free.background,
                                    }}
                                  />
                                )}
                                <Chip
                                  label={statusMeta.label}
                                  size="small"
                                  sx={{
                                    color: statusMeta.textColor,
                                    border: `1px solid ${statusMeta.borderColor}`,
                                    bgcolor: statusMeta.background,
                                  }}
                                />
                              </Stack>
                            </Stack>

                            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                              {buildPickLabel(note)}
                            </Typography>

                            <Typography
                              sx={{ color: 'rgba(200, 228, 245, 0.74)', fontSize: 12, lineHeight: 1.7 }}
                            >
                              自信度 {note.confidence_label || '-'} / 更新 {formatDateTime(note.updated_at)}
                            </Typography>

                            <Typography
                              sx={{
                                color: 'rgba(200, 228, 245, 0.62)',
                                fontSize: 12,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {note.note_url || 'URL未設定'}
                            </Typography>

                            {buildPublicNoteUrl(note) && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopy(buildAnnouncementMessage(note), '投稿文をコピーしました。');
                                }}
                                endIcon={<ContentCopyRoundedIcon />}
                                sx={{
                                  alignSelf: 'flex-start',
                                  borderRadius: 999,
                                  color: '#9ed7ff',
                                  borderColor: 'rgba(92, 160, 219, 0.30)',
                                  fontSize: 12,
                                  py: 0.4,
                                  px: 1.5,
                                  textTransform: 'none',
                                  minWidth: 0,
                                }}
                              >
                                投稿文コピー
                              </Button>
                            )}
                          </Stack>
                        </Button>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </Paper>

            <Paper
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 4,
                color: 'inherit',
                border: '1px solid rgba(92, 160, 219, 0.2)',
                background:
                  'linear-gradient(180deg, rgba(10,24,41,0.92), rgba(4,10,20,0.95))',
              }}
            >
              {selectedNote ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      sx={{
                        color: '#71dbff',
                        fontSize: 12,
                        letterSpacing: '0.24em',
                        textTransform: 'uppercase',
                      }}
                    >
                      Note Detail
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: { xs: 22, sm: 28 }, fontWeight: 700 }}>
                      {selectedNote.title || `${selectedNote.place_name} ${selectedNote.race_label}`}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip
                      label={getStatusMeta(selectedNote.status).label}
                      sx={{
                        color: getStatusMeta(selectedNote.status).textColor,
                        bgcolor: getStatusMeta(selectedNote.status).background,
                      }}
                    />
                    {selectedNote.is_paid !== undefined && (
                      <Chip
                        label={selectedNote.is_paid ? PAID_META.paid.label : PAID_META.free.label}
                        sx={{
                          color: selectedNote.is_paid ? PAID_META.paid.textColor : PAID_META.free.textColor,
                          bgcolor: selectedNote.is_paid ? PAID_META.paid.background : PAID_META.free.background,
                        }}
                      />
                    )}
                    <Chip
                      label={`自信度 ${selectedNote.confidence_label || '-'}`}
                      sx={{ color: '#eef8ff', bgcolor: 'rgba(56, 104, 162, 0.28)' }}
                    />
                    <Chip
                      label={`更新 ${formatDateTime(selectedNote.updated_at)}`}
                      sx={{ color: '#eef8ff', bgcolor: 'rgba(56, 104, 162, 0.28)' }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
                    {buildInternalNoteUrl(selectedNote) && (
                      <Button
                        variant="contained"
                        href={buildInternalNoteUrl(selectedNote)}
                        target="_blank"
                        rel="noreferrer"
                        endIcon={<OpenInNewRoundedIcon />}
                        sx={{ borderRadius: 999 }}
                      >
                        内部参照
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      onClick={() =>
                        handleCopy(buildPublicNoteUrl(selectedNote), '公開URLをコピーしました。')
                      }
                      disabled={!buildPublicNoteUrl(selectedNote)}
                      endIcon={<ContentCopyRoundedIcon />}
                      sx={{ borderRadius: 999, color: '#9ed7ff', borderColor: 'rgba(92, 160, 219, 0.22)' }}
                    >
                      公開URLコピー
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        handleCopy(buildAnnouncementMessage(selectedNote), '投稿文をコピーしました。')
                      }
                      disabled={!buildPublicNoteUrl(selectedNote)}
                      endIcon={<ContentCopyRoundedIcon />}
                      sx={{ borderRadius: 999, color: '#9ed7ff', borderColor: 'rgba(92, 160, 219, 0.22)' }}
                    >
                      投稿文コピー
                    </Button>
                    {selectedNote.article_markdown && (
                      <Button
                        variant="outlined"
                        onClick={() =>
                          handleCopy(selectedNote.article_markdown, '予想内容をコピーしました。')
                        }
                        endIcon={<ContentCopyRoundedIcon />}
                        sx={{ borderRadius: 999, color: '#9ed7ff', borderColor: 'rgba(92, 160, 219, 0.22)' }}
                      >
                        予想内容コピー
                      </Button>
                    )}
                    {selectedNote.public_url && (
                      <Button
                        variant="outlined"
                        href={selectedNote.public_url}
                        target="_blank"
                        rel="noreferrer"
                        endIcon={<OpenInNewRoundedIcon />}
                        sx={{ borderRadius: 999, color: '#9ed7ff', borderColor: 'rgba(92, 160, 219, 0.22)' }}
                      >
                        公開URL
                      </Button>
                    )}
                    {selectedNote.editor_url && (
                      <Button
                        variant="outlined"
                        href={selectedNote.editor_url}
                        target="_blank"
                        rel="noreferrer"
                        endIcon={<OpenInNewRoundedIcon />}
                        sx={{ borderRadius: 999, color: '#9ed7ff', borderColor: 'rgba(92, 160, 219, 0.22)' }}
                      >
                        編集URL
                      </Button>
                    )}
                    {selectedNote.publish_url && (
                      <Button
                        variant="outlined"
                        href={selectedNote.publish_url}
                        target="_blank"
                        rel="noreferrer"
                        endIcon={<OpenInNewRoundedIcon />}
                        sx={{ borderRadius: 999, color: '#9ed7ff', borderColor: 'rgba(92, 160, 219, 0.22)' }}
                      >
                        Publish画面
                      </Button>
                    )}
                  </Stack>

                  {copyStatus && (
                    <Typography sx={{ color: '#96f3d0', fontSize: 13 }}>{copyStatus}</Typography>
                  )}
                  {!buildPublicNoteUrl(selectedNote) && (
                    <Typography sx={{ color: '#ffe6a7', fontSize: 13 }}>
                      公開URLが未取得のため、ユーザー向けURLの案内とコピーはまだできません。
                    </Typography>
                  )}

                  <Divider sx={{ borderColor: 'rgba(92, 160, 219, 0.16)' }} />

                  <Stack spacing={1.5}>
                    <FieldRow
                      label="レース"
                      value={`${selectedNote.date} / ${selectedNote.place_name} ${selectedNote.race_label}`}
                    />
                    <FieldRow
                      label="本命馬"
                      value={buildPickLabel(selectedNote)}
                    />
                    <FieldRow
                      label="公開URL"
                      value={buildPublicNoteUrl(selectedNote) || '未取得'}
                      mono
                    />
                    <FieldRow
                      label="内部参照URL"
                      value={buildInternalNoteUrl(selectedNote) || '未取得'}
                      mono
                    />
                    <FieldRow label="公開日時" value={formatDateTime(selectedNote.published_at)} />
                    <FieldRow label="note_id" value={selectedNote.note_id ?? '-'} />
                    <FieldRow label="投稿文プレビュー" value={buildAnnouncementMessage(selectedNote)} />
                    {selectedNote.status === 'error' && selectedNote.error && (
                      <FieldRow label="エラー内容" value={selectedNote.error} />
                    )}
                    <FieldRow label="article_json" value={selectedNote.article_json} mono />
                    <FieldRow label="article_markdown" value={selectedNote.article_markdown} mono />
                    <FieldRow label="artifact_screenshot" value={selectedNote.artifact_screenshot} mono />
                    <FieldRow label="source_run_dir" value={selectedNote.source_run_dir} mono />
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={1.5} justifyContent="center" sx={{ minHeight: 320 }}>
                  <Typography
                    sx={{
                      color: '#71dbff',
                      fontSize: 12,
                      letterSpacing: '0.24em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Detail Standby
                  </Typography>
                  <Typography sx={{ fontSize: { xs: 22, sm: 28 }, fontWeight: 700 }}>
                    Note を選択すると詳細を表示します
                  </Typography>
                </Stack>
              )}
            </Paper>
          </Box>

          <LegalFooter />
        </Stack>
      </Box>
    </Box>
  );
}

export default NotePostsPage;
